<template>
  <div>
    <loading-placeholder v-if="!projectsLoaded" :lines="3" />
    <template v-else>
      <div v-if="!hasProjects" class="add-global-task__no-projects">
        <h4>You don&rsquo;t belong to a project yet.</h4>
        <span>You can add tasks once you belong to a project.</span>
        <new-project-button size="medium" />
      </div>
      <!-- SELECT PROJECT -->
      <div v-else class="add-global-task__project-switcher">
        <h4 v-if="!hasSelectedProjectID">Select a project for this task…</h4>
        <single-select v-model="selectedProjectID" placeholder="Select project…">
          <el-option v-for="project in projects" :key="project.id" :label="project.name" :value="project.id">
            <i :class="`ddi ddi-${getProjectIcon(project)}`" /><span>{{project.name}}</span>
          </el-option>
        </single-select>
      </div>
      <!-- CREATE TASK -->
      <add-task-form
        v-if="showForm"
        :lives-in-drawer="livesInDrawer"
        :selected-project-id="selectedProjectID"
        :originating-conversation-id="originatingConversationID"
        :originating-task-id="originatingTaskID"
        :default-description="defaultDescription"
        :custom-sticky-top-pixels="-35"
        @task-created="(id, createAnother) => $emit('task-created', selectedProjectID, id, createAnother)"
        @title-desc-modified="$emit('task-desc-modified')"
        @title-desc-reset="$emit('task-desc-reset')"
        @canceled="handleCancel()"
      />
    </template>
  </div>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import {
  IItemReference,
  IProjectForSelection,
} from '@/api/types/response.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'AddGlobalTask',

  props: {
    originatingConversation: {
      type: Object as () => IItemReference,
      default: null,
    },
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    originatingItemEventId: {
      type: Number,
      default: null,
    },
    // Default project to select after projects dropdown is loaded
    defaultProjectId: {
      type: Number,
      default: null,
    },
    livesInDrawer: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['canceled', 'task-created', 'task-desc-modified', 'task-desc-reset'],

  data() {
    return {
      isLoading: false,
      selectedProjectID: null as any,
      projectsLoaded: false,
      showForm: false,
      projects: [] as IProjectForSelection[],
      closeDrawerOnOutsideClick: true,
      defaultDescription: null as any,
      lastLinkedTaskInternalProjectIDForConvo: null as any,
    }
  },

  computed: {
    originatingConversationID(): number {
      if (this.originatingConversation) {
        return this.originatingConversation.id
      }
      return null
    },
    originatingTaskID(): number {
      if (this.originatingTask) {
        return this.originatingTask.id
      }
      return null
    },
    hasSelectedProjectID(): boolean {
      return this.selectedProjectID !== null
    },
    hasProjects(): boolean {
      return this.projects !== null && this.projects.length > 0
    },
  },

  watch: {
    selectedProjectID: {
      handler(value) {
        if (value !== null) {
          this.showForm = true
        }
      },
    },
  },

  async created() {
    await this.getPotentialDefaults()
    if (!this.projectsLoaded) {
      await this.fetchProjects()

      // Hydrate here so form is not exposed while projects are
      // loading (via selectedProjectID watch method -> showForm = true)...
      if (this.defaultProjectId) {
        if (this.projects.map(p => p.id).includes(this.defaultProjectId)) {
          this.selectedProjectID = this.defaultProjectId
        }
      } else if (this.lastLinkedTaskInternalProjectIDForConvo) {
        if (
          this.projects
            .map(p => p.id)
            .includes(this.lastLinkedTaskInternalProjectIDForConvo)
        ) {
          this.selectedProjectID = this.lastLinkedTaskInternalProjectIDForConvo
        }
      } else if (this.projects.length === 1) {
        this.selectedProjectID = this.projects[0].id
      }
      this.projectsLoaded = true
    }
  },

  methods: {
    getProjectIcon(project: IProjectForSelection) {
      return project.isFavorite ? 'star-filled' : 'project'
    },

    async fetchProjects() {
      try {
        this.projects = await InternalProjectsAPI.get_ForSelection()
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    // Will hydrate default description if originatingItemEventId
    // Will attempt to hydrate project ID if originatingConvo exists
    async getPotentialDefaults() {
      if (this.originatingConversationID) {
        const response = await MailboxesAPI.get_LastLinkedTaskInternalProjectID(
          this.$route.params.id,
        )
        if (response.internalProjectID) {
          this.lastLinkedTaskInternalProjectIDForConvo =
            response.internalProjectID
        }
      }

      if (!this.originatingItemEventId) {
        return
      }

      try {
        if (this.originatingTask) {
          const response = await TasksAPI.get_EventDescription(
            this.$route.params.id,
            this.originatingTaskID,
            this.originatingItemEventId,
          )
          // Clean up extra returns common to many emailed replies
          this.defaultDescription = TextAssistant.removeNonRTEBreaks(
            response.description,
          )
        } else {
          const response = await ConversationsAPI.get_EventDescription(
            this.$route.params.id,
            this.originatingConversationID,
            this.originatingItemEventId,
          )
          // Clean up extra returns common to many emailed replies
          this.defaultDescription = TextAssistant.removeNonRTEBreaks(
            response.description,
          )
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    handleCancel() {
      this.$emit('canceled')
    },
  },
})
</script>

<style lang="scss">
.add-global-task {
  &__project-switcher {
    width: 50%;
    min-width: 320px;
    padding-bottom: 20px;

    @include media-breakpoint-down(sm) {
      width: 100%;
      min-width: 100%;
    }
  }

  &__no-projects {
    display: flex;
    flex-direction: column;
    align-items: center;

    > h2 {
      font-weight: 500;
      padding-bottom: 0;
      margin-bottom: 4px;
    }

    > button {
      margin-top: 20px;
    }
  }
}
</style>
